import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FileText,
  Bot,
  Users,
  Upload,
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ClipboardList,
  Truck,
  FolderKanban,
  UserCircle,
  Package,
  Calculator,
  Receipt,
  UsersRound,
  CalendarDays,
  ShoppingCart,
  Inbox,
  BarChart3,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { isAdmin } from '../utils/permissions';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  roles?: string[];
  section?: string;
}

const FINANCE_ROLES = ['ADMIN', 'ACCOUNTING', 'MANAGER', 'ENGINEERING'];

const navItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },

  // Comercial
  { path: '/clients', label: 'Clientes', icon: UserCircle, section: 'Comercial' },
  { path: '/quotations', label: 'Cotizaciones', icon: ClipboardList },
  { path: '/suppliers', label: 'Proveedores', icon: Truck },
  { path: '/budgets', label: 'Presupuestos', icon: Calculator },

  { path: '/purchase-orders', label: 'Órdenes de Compra', icon: ShoppingCart },

  // Proyectos
  { path: '/projects', label: 'Proyectos', icon: FolderKanban, section: 'Operaciones' },
  { path: '/inventory', label: 'Inventario', icon: Package },

  // Finanzas
  { path: '/invoices', label: 'Facturación', icon: Receipt, section: 'Finanzas' },
  { path: '/invoice-inbox', label: 'Bandeja de Facturas', icon: Inbox, roles: FINANCE_ROLES },
  { path: '/project-finance', label: 'Control Financiero', icon: BarChart3, roles: FINANCE_ROLES },

  // RRHH
  { path: '/hr', label: 'Recursos Humanos', icon: UsersRound, section: 'RRHH', adminOnly: true },

  // Herramientas
  { path: '/calendar', label: 'Calendario', icon: CalendarDays, section: 'Herramientas' },
  { path: '/documents', label: 'Documentos', icon: FileText },
  { path: '/assistant', label: 'Asistente IA', icon: Bot },

  // Admin
  { path: '/admin/users', label: 'Usuarios', icon: Users, adminOnly: true, section: 'Admin' },
  { path: '/admin/documents', label: 'Gestionar Docs', icon: Upload, adminOnly: true },
];

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter((item) => {
    if (item.adminOnly && !(user && isAdmin(user.role))) return false;
    if (item.roles && !(user && item.roles.includes(user.role))) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 border-r border-slate-800 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          <Link to="/" className="flex items-center gap-3">
            <img src="/inntea-brand-white.png" alt="INNTEA S.A.S." className="h-9 w-auto" />
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-0.5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
          {filteredNavItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            const showSection = item.section && (index === 0 || filteredNavItems[index - 1]?.section !== item.section);

            return (
              <div key={item.path + item.label}>
                {showSection && (
                  <p className={`text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 ${index > 0 ? 'mt-4' : ''} mb-2`}>
                    {item.section}
                  </p>
                )}
                <Link
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium text-sm">{item.label}</span>
                </Link>
              </div>
            );
          })}
        </nav>

        {/* User info at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2">
            <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-slate-300">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">
                {{ ADMIN: 'Administrador', ENGINEERING: 'Ingeniería', COMMERCIAL: 'Comercial', ACCOUNTING: 'Contabilidad', HR: 'RRHH', MANAGER: 'Gerente' }[user?.role || 'ENGINEERING']}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-slate-400 hover:text-rose-400 transition-colors"
              title="Cerrar sesión"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="h-16 bg-slate-900/80 backdrop-blur border-b border-slate-800 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 text-slate-400 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex-1 lg:flex-none" />

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors"
            >
              <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-slate-300">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="hidden sm:block text-sm text-slate-300">{user?.name}</span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>

            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-2 w-48 bg-slate-800 rounded-lg shadow-xl border border-slate-700 z-50">
                  <div className="p-3 border-b border-slate-700">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-slate-400">{user?.email}</p>
                  </div>
                  <div className="p-1">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-400 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Cerrar sesión
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
